// नए शब्द: every distinct word of the story under its dictionary form (from the analyser), with its meaning, the forms
// that occur in the story (tappable) and a link to the headword page; then the story's pre-taught phrases.

import type { FC, ReactNode } from 'react'
import type { PreTaughtPhrase, StoryWord } from '@/prakrit/components/stories/story-logic'

import Link from 'next/link'
import { BookMarked, ChevronRight, Sparkles } from 'lucide-react'
import { PrakritWord } from '@/prakrit/components/common/prakrit-word'
import { Bi } from '@/prakrit/components/common/bi'
import { hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'

const WordRow: FC<{ word: StoryWord }> = ({ word }): ReactNode => {
    const others = word.forms.filter(form => form !== word.label)

    return (
        <li className='flex flex-col gap-1.5 py-3.5'>
            <div className='flex flex-wrap items-baseline gap-x-2 gap-y-1'>
                {word.href ? (
                    <Link
                        href={word.href}
                        lang='pra'
                        className='group inline-flex items-baseline gap-1 rounded-sm font-prakrit text-[1.3125rem] font-semibold text-prakrit underline decoration-prakrit/30 underline-offset-[0.28em] hover:decoration-saffron in-data-[mode=kids]:text-[1.5rem]'
                    >
                        {word.label}
                        <ChevronRight className='size-4 self-center text-muted-foreground transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                        <span className='sr-only'> — शब्दकोश में देखें · open in the dictionary</span>
                    </Link>
                ) : (
                    <span lang='pra' className='font-prakrit text-[1.3125rem] font-semibold text-prakrit in-data-[mode=kids]:text-[1.5rem]'>{word.label}</span>
                )}
                <span lang='pra-Latn' className='translit text-xs italic text-muted-foreground'>{word.translit}</span>
            </div>
            <Bi hi={word.meaning.hi} en={word.meaning.en} layout='inline' className='text-[0.9375rem]' enClassName='text-muted-foreground' />
            <ul className='flex flex-wrap gap-1.5'>
                {word.tags.map(tag => (
                    <li key={`${tag.hi}-${tag.en}`} className='rounded-md bg-secondary px-2 py-0.5 text-xs'>
                        <span lang='hi' className='hindi font-medium'>{tag.hi}</span>
                        <span lang='en' data-ex='en' className='text-muted-foreground'> · {tag.en}</span>
                    </li>
                ))}
            </ul>
            {others.length > 0 && (
                <p className='flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm'>
                    <span lang='hi' className='hindi text-muted-foreground'>कथा में:</span>
                    <span lang='en' data-ex='en' className='sr-only'>in the story:</span>
                    {others.map(form => (
                        <span key={form} className='font-prakrit text-base font-semibold text-prakrit'>
                            <PrakritWord word={form} />
                        </span>
                    ))}
                </p>
            )}
        </li>
    )
}

interface StoryWordsProps {
    words: StoryWord[]
    preTaught: PreTaughtPhrase[]
}

export const StoryWords: FC<StoryWordsProps> = ({ words, preTaught }): ReactNode => (
    <div className='flex flex-col gap-6'>
        <section aria-labelledby='story-words' className='rounded-xl border border-border bg-card p-5 shadow-card'>
            <h2 id='story-words' className='flex flex-wrap items-baseline gap-x-2'>
                <BookMarked className='size-5 self-center text-saffron-strong' aria-hidden='true' />
                <span lang='hi' className='hindi text-xl font-bold'>नए शब्द</span>
                <span lang='en' className='font-sans text-sm font-medium text-muted-foreground'>New words</span>
                <span className='ml-auto text-sm text-muted-foreground'>
                    <span lang='hi' className='hindi'>{hiDigits(words.length)}</span>
                    <span className='sr-only'> · {words.length} words</span>
                </span>
            </h2>
            <p className='mt-1 text-sm'>
                <Bi
                    hi='इस कथा का हर शब्द, उसके मूल रूप (धातु या शब्द) के साथ।'
                    en='Every word of this story, under its root or stem.'
                    enClassName='text-muted-foreground'
                />
            </p>
            {words.length === 0 ? (
                <p className='mt-4 text-sm text-muted-foreground'>
                    <Bi hi='इस कथा के शब्दों का विश्लेषण अभी उपलब्ध नहीं है।' en='The words of this story cannot be analysed yet.' />
                </p>
            ) : (
                <ul className='mt-2 flex flex-col divide-y divide-border'>
                    {words.map(word => <WordRow key={word.key} word={word} />)}
                </ul>
            )}
        </section>

        {preTaught.length > 0 && (
            <section aria-labelledby='story-pretaught' className='rounded-xl border border-saffron/40 bg-saffron/6 p-5'>
                <h2 id='story-pretaught' className='flex flex-wrap items-baseline gap-x-2'>
                    <Sparkles className='size-5 self-center text-saffron-strong' aria-hidden='true' />
                    <span lang='hi' className='hindi text-lg font-bold'>पहले से समझाए गए रूप</span>
                    <span lang='en' className='font-sans text-sm font-medium text-muted-foreground'>Pre-taught phrases</span>
                </h2>
                <p className='mt-1 text-sm'>
                    <Bi
                        hi='ये रूप व्याकरण में बाद में आते हैं; इस कथा में इन्हें पूरे वाक्यांश की तरह समझिए।'
                        en='These forms are taught later in the course; in this story, learn them as whole phrases.'
                        enClassName='text-muted-foreground'
                    />
                </p>
                <ul className='mt-3 flex flex-col gap-3'>
                    {preTaught.map(phrase => (
                        <li key={phrase.form} className='flex flex-col gap-1 rounded-lg bg-card px-3.5 py-3'>
                            <p className='flex flex-wrap items-baseline gap-x-2'>
                                <span className='font-prakrit text-[1.1875rem] font-semibold text-prakrit'>
                                    <PrakritWord word={phrase.form} />
                                </span>
                                <Bi hi={phrase.hi} en={phrase.en} layout='inline' className='text-sm' enClassName='text-muted-foreground' />
                            </p>
                            <p className='text-sm'>
                                <span lang='hi' data-ex='hi' className='hindi block text-foreground/85'>{phrase.note.hi}</span>
                                <span lang='en' data-ex='en' className='block text-muted-foreground'>{phrase.note.en}</span>
                            </p>
                        </li>
                    ))}
                </ul>
            </section>
        )}
    </div>
)
